import { useRef, useState } from 'react';
import { Loader2, Upload, FileText, Eye, Download, RefreshCw, Trash2 } from 'lucide-react';
import {
    useGetFilteredDocumentsQuery,
    useUploadInvestigationDocumentMutation,
    useDeleteDocumentMutation,
} from '@/store/api/apiSlice';
import { resolveFileUrl } from '@/utils/fileUrl';
import { validateInvestigationFile } from '@/utils/fileValidation';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import type { DocumentType, InvestigationDocument } from '@/types';

// Full document type list — staff can upload/overwrite any of these.
const DOCUMENT_TYPES: { value: string; label: string }[] = [
    { value: 'agent_report', label: 'Agent Report' },
    { value: 'investigation_report', label: 'Investigation Report' },
    { value: 'signed_affidavit', label: 'Signed Affidavit' },
    { value: 'notarized_affidavit', label: 'Notarized Affidavit' },
    { value: 'police_character_certificate', label: 'Police Character Certificate' },
    { value: 'social_media_report', label: 'Social Media Report' },
    { value: 'cv', label: 'CV' },
    { value: 'national_id', label: 'National ID' },
    { value: 'nin', label: 'NIN' },
    { value: 'passport', label: 'Passport' },
    { value: 'letter_of_employment', label: 'Letter of Employment' },
    { value: 'other', label: 'Other' },
];

const TYPE_LABEL: Record<string, string> = Object.fromEntries(DOCUMENT_TYPES.map((t) => [t.value, t.label]));

interface Props {
    investigationId: string;
    // Whether this role may upload / replace / delete. When false, the panel is
    // read-only (view + download).
    canManage: boolean;
}

export default function InvestigationDocumentsPanel({ investigationId, canManage }: Props) {
    const { data: documents = [], isLoading } = useGetFilteredDocumentsQuery(
        { id: investigationId },
        { skip: !investigationId },
    );
    const [uploadDoc, { isLoading: isUploading }] = useUploadInvestigationDocumentMutation();
    const [deleteDoc, { isLoading: isDeleting }] = useDeleteDocumentMutation();

    const [docType, setDocType] = useState<string>('other');
    const [error, setError] = useState('');
    const uploadInputRef = useRef<HTMLInputElement>(null);
    const replaceInputRef = useRef<HTMLInputElement>(null);
    const replaceTypeRef = useRef<string>('other');

    const upload = async (file: File, documentType: string) => {
        const validationError = validateInvestigationFile(file);
        if (validationError) {
            setError(validationError);
            return;
        }
        setError('');
        try {
            await uploadDoc({ id: investigationId, file, documentType: documentType as DocumentType }).unwrap();
        } catch (err) {
            setError(parseErrorMessage(err, 'Could not upload the document. Please try again.'));
        }
    };

    const handleUploadSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (file) await upload(file, docType);
    };

    const openReplace = (documentType: string) => {
        replaceTypeRef.current = documentType || 'other';
        replaceInputRef.current?.click();
    };

    const handleReplaceSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (file) await upload(file, replaceTypeRef.current);
    };

    const handleDelete = async (doc: InvestigationDocument) => {
        if (!window.confirm(`Delete "${doc.fileName}"? This cannot be undone.`)) return;
        setError('');
        try {
            await deleteDoc({ investigationId, documentId: doc.id }).unwrap();
        } catch (err) {
            setError(parseErrorMessage(err, 'Could not delete the document.'));
        }
    };

    const openDoc = (doc: InvestigationDocument) => {
        const url = doc.downloadUrl || doc.fileUrl;
        if (url) window.open(resolveFileUrl(url), '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h3 className="text-sm font-bold text-[#1b1b1b]">Documents</h3>
                    <p className="mt-0.5 text-xs text-[#4e4d4d]">
                        Upload or overwrite any document for this investigation.
                    </p>
                </div>
            </div>

            {canManage && (
                <div className="flex flex-col gap-2 rounded-xl border border-[#e0e0e0] p-3 sm:flex-row sm:items-end">
                    <div className="flex-1">
                        <label className="mb-1 block text-xs font-medium text-[#4e4d4d]">Document type</label>
                        <select
                            value={docType}
                            onChange={(e) => setDocType(e.target.value)}
                            className="h-10 w-full rounded-lg border border-[#e0e0e0] bg-white px-3 text-sm outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15"
                        >
                            {DOCUMENT_TYPES.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <input
                        ref={uploadInputRef}
                        type="file"
                        accept="application/pdf,.pdf,.doc,.docx,image/jpeg,image/png"
                        onChange={handleUploadSelected}
                        className="hidden"
                    />
                    <input
                        ref={replaceInputRef}
                        type="file"
                        accept="application/pdf,.pdf,.doc,.docx,image/jpeg,image/png"
                        onChange={handleReplaceSelected}
                        className="hidden"
                    />
                    <button
                        type="button"
                        onClick={() => uploadInputRef.current?.click()}
                        disabled={isUploading}
                        className="flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg bg-[#FF6B35] px-5 text-sm font-medium text-white hover:bg-[#FF5722] disabled:opacity-60"
                    >
                        {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        Upload
                    </button>
                </div>
            )}

            {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
            )}

            {isLoading ? (
                <div className="flex justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-[#FF6B35]" />
                </div>
            ) : documents.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[#e0e0e0] bg-[#fafafa] py-8 text-center">
                    <FileText className="mx-auto mb-2 h-5 w-5 text-[#9a9a9a]" />
                    <p className="text-sm text-[#4e4d4d]">No documents uploaded yet.</p>
                </div>
            ) : (
                <ul className="flex flex-col gap-2">
                    {documents.map((doc) => (
                        <li
                            key={doc.id}
                            className="flex items-center gap-3 rounded-lg border border-[#e0e0e0] px-3 py-2.5"
                        >
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#FF6B35]/10">
                                <FileText className="h-4 w-4 text-[#FF6B35]" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-[#1b1b1b]">{doc.fileName}</p>
                                <p className="text-[11px] text-[#717680]">
                                    {TYPE_LABEL[String(doc.documentType)] ?? String(doc.documentType)}
                                </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => openDoc(doc)}
                                    title="View"
                                    className="rounded p-1.5 text-[#4e4d4d] hover:bg-gray-100 hover:text-[#1b1b1b]"
                                >
                                    <Eye className="h-4 w-4" />
                                </button>
                                <a
                                    href={resolveFileUrl(doc.downloadUrl || doc.fileUrl)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Download"
                                    className="rounded p-1.5 text-[#4e4d4d] hover:bg-gray-100 hover:text-[#1b1b1b]"
                                >
                                    <Download className="h-4 w-4" />
                                </a>
                                {canManage && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => openReplace(String(doc.documentType))}
                                            disabled={isUploading}
                                            title="Replace / overwrite"
                                            className="rounded p-1.5 text-[#4e4d4d] hover:bg-gray-100 hover:text-[#FF6B35] disabled:opacity-50"
                                        >
                                            <RefreshCw className="h-4 w-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(doc)}
                                            disabled={isDeleting}
                                            title="Delete"
                                            className="rounded p-1.5 text-[#4e4d4d] hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
